import type {
  SanitizedTelemetryIdentity,
  SanitizedTelemetryProperties,
  TelemetryScalar,
} from "./events";

export const TELEMETRY_REDACTED_VALUE = "[redacted]";

const MAX_STRING_LENGTH = 500;
const MAX_CONTEXT_DEPTH = 4;
const MAX_CONTEXT_ARRAY_ITEMS = 20;
const MAX_CONTEXT_OBJECT_KEYS = 50;

const EVM_ADDRESS_PATTERN = /\b0x[a-fA-F0-9]{40}\b/u;
const EVM_ADDRESS_REPLACEMENT_PATTERN = /\b0x[a-fA-F0-9]{40}\b/gu;
const SOLANA_ADDRESS_PATTERN = /\b[1-9A-HJ-NP-Za-km-z]{32,44}\b/u;
const SOLANA_ADDRESS_REPLACEMENT_PATTERN = /\b[1-9A-HJ-NP-Za-km-z]{32,44}\b/gu;
const UUID_PATTERN =
  /\b[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}\b/u;
const UUID_REPLACEMENT_PATTERN =
  /\b[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}\b/gu;
const CANONICAL_USER_ID_PATTERN =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/u;
const PRIVY_USER_ID_PATTERN = /^did:privy:[A-Za-z0-9._:-]{1,256}$/u;
const EVM_WALLET_ADDRESS_PATTERN = /^0x[a-fA-F0-9]{40}$/u;
const SOLANA_WALLET_ADDRESS_PATTERN = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/u;

const SAFE_IDENTIFIER_KEYS = new Set([
  "auth_source",
  "authenticated",
  "provider",
  "route_visit_id",
  "route_id",
  "route_path",
  "session_replay_enabled",
  "vm_preference",
]);

const SENSITIVE_KEY_PARTS = [
  "address",
  "allocation",
  "amount",
  "authorization",
  "balance",
  "blueprint",
  "cookie",
  "email",
  "holdings",
  "onboarding",
  "portfolio",
  "price",
  "private",
  "prompt",
  "query",
  "request_body",
  "response_body",
  "search",
  "secret",
  "signature",
  "strategy_instance",
  "task_id",
  "token",
  "user_id",
  "wallet",
];

export type SanitizedTelemetryContextValue =
  | TelemetryScalar
  | SanitizedTelemetryContextValue[]
  | { [key: string]: SanitizedTelemetryContextValue };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isTelemetryScalar(value: unknown): value is TelemetryScalar {
  return (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

function truncateTelemetryString(value: string): string {
  if (value.length <= MAX_STRING_LENGTH) {
    return value;
  }

  return `${value.slice(0, MAX_STRING_LENGTH)}...`;
}

export function containsSensitiveIdentifier(value: string): boolean {
  return (
    EVM_ADDRESS_PATTERN.test(value) ||
    SOLANA_ADDRESS_PATTERN.test(value) ||
    UUID_PATTERN.test(value)
  );
}

export function redactTelemetryString(value: string): string {
  return truncateTelemetryString(
    value
      .replace(EVM_ADDRESS_REPLACEMENT_PATTERN, TELEMETRY_REDACTED_VALUE)
      .replace(SOLANA_ADDRESS_REPLACEMENT_PATTERN, TELEMETRY_REDACTED_VALUE)
      .replace(UUID_REPLACEMENT_PATTERN, TELEMETRY_REDACTED_VALUE),
  );
}

export function isSensitiveTelemetryKey(key: string): boolean {
  const normalized = key
    .replace(/[A-Z]/gu, (match) => `_${match.toLowerCase()}`)
    .toLowerCase();
  if (SAFE_IDENTIFIER_KEYS.has(normalized)) {
    return false;
  }

  if (
    normalized === "id" ||
    normalized.endsWith("_id") ||
    normalized.endsWith("_ids")
  ) {
    return true;
  }

  return SENSITIVE_KEY_PARTS.some((part) => normalized.includes(part));
}

function sanitizeTelemetryScalarValue(value: TelemetryScalar): TelemetryScalar {
  if (typeof value !== "string") {
    return value;
  }

  return redactTelemetryString(value);
}

export function sanitizeTelemetryProperties(
  input: Readonly<Record<string, unknown>>,
): SanitizedTelemetryProperties {
  const properties: SanitizedTelemetryProperties = {};

  for (const [key, value] of Object.entries(input)) {
    if (value === undefined) {
      continue;
    }

    if (isSensitiveTelemetryKey(key)) {
      properties[key] = TELEMETRY_REDACTED_VALUE;
      continue;
    }

    if (isTelemetryScalar(value)) {
      properties[key] = sanitizeTelemetryScalarValue(value);
    }
  }

  return properties;
}

export function sanitizeTelemetryIdentity(input: {
  authenticated: boolean;
  authSource: string;
  canonicalUserId: string | null;
  privyUserId: string | null;
  walletAddress: string | null;
}): SanitizedTelemetryIdentity {
  const canonicalUserId =
    input.authenticated &&
    input.canonicalUserId &&
    CANONICAL_USER_ID_PATTERN.test(input.canonicalUserId)
      ? input.canonicalUserId.toLowerCase()
      : null;
  const privyUserId =
    canonicalUserId &&
    input.authSource === "privy" &&
    input.privyUserId &&
    PRIVY_USER_ID_PATTERN.test(input.privyUserId)
      ? input.privyUserId
      : null;
  const walletAddress =
    canonicalUserId &&
    input.walletAddress &&
    (EVM_WALLET_ADDRESS_PATTERN.test(input.walletAddress) ||
      SOLANA_WALLET_ADDRESS_PATTERN.test(input.walletAddress))
      ? input.walletAddress
      : null;

  return {
    authenticated: input.authenticated,
    authSource: redactTelemetryString(input.authSource),
    canonicalUserId,
    privyUserId,
    walletAddress,
  };
}

export function sanitizeTelemetryContextValue(
  value: unknown,
  depth = 0,
): SanitizedTelemetryContextValue {
  if (value instanceof URL) {
    return redactUrlForTelemetry(value.toString());
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (isTelemetryScalar(value)) {
    return sanitizeTelemetryScalarValue(value);
  }

  if (Array.isArray(value)) {
    if (depth >= MAX_CONTEXT_DEPTH) {
      return TELEMETRY_REDACTED_VALUE;
    }

    return value
      .slice(0, MAX_CONTEXT_ARRAY_ITEMS)
      .map((item) => sanitizeTelemetryContextValue(item, depth + 1));
  }

  if (isRecord(value)) {
    if (depth >= MAX_CONTEXT_DEPTH) {
      return TELEMETRY_REDACTED_VALUE;
    }

    const sanitized: { [key: string]: SanitizedTelemetryContextValue } = {};
    for (const [key, item] of Object.entries(value).slice(
      0,
      MAX_CONTEXT_OBJECT_KEYS,
    )) {
      sanitized[key] = isSensitiveTelemetryKey(key)
        ? TELEMETRY_REDACTED_VALUE
        : sanitizeTelemetryContextValue(item, depth + 1);
    }

    return sanitized;
  }

  return TELEMETRY_REDACTED_VALUE;
}

export function sanitizeTelemetryContextRecord(
  value: Readonly<Record<string, unknown>>,
): Record<string, SanitizedTelemetryContextValue> {
  const sanitized: Record<string, SanitizedTelemetryContextValue> = {};
  for (const [key, item] of Object.entries(value)) {
    sanitized[key] = isSensitiveTelemetryKey(key)
      ? TELEMETRY_REDACTED_VALUE
      : sanitizeTelemetryContextValue(item);
  }

  return sanitized;
}

export function redactUrlForTelemetry(rawUrl: string): string {
  const baseUrl =
    typeof window === "undefined"
      ? "https://app.glider.fi"
      : window.location.origin;

  try {
    const parsed = new URL(rawUrl, baseUrl);
    const pathname = redactTelemetryString(parsed.pathname);
    const origin =
      parsed.origin === baseUrl || rawUrl.startsWith("/") ? "" : parsed.origin;
    return `${origin}${pathname}`;
  } catch {
    return redactTelemetryString(rawUrl.split(/[?#]/u, 1)[0] ?? "");
  }
}
